import { useEffect, useState } from "react";
import { observer } from "mobx-react";
import BlockTitle from "../_BlockTitle";
import styles from "./styles/AccordionTerrace.module.scss";
import classNames from "classnames";
import FontAwesome from "../../../utilities/FontAwesome";
import AccordionItemEditor from "./AccordionItemEditor";
import AccordionItem from "./AccordionItem";
import { EditDropDownItem } from "../EditDropDown";
import { AccordionItemSearchResultList } from "./AccordionItemSearchResultList";
import SearchBox from "../../../../pages/SearchPage/SearchBox";
import AccordionReorderingTheme from "./AccordionReorderingTheme";
import {
  getUpdatedItemPathsFromMove,
  numExpandedNodes,
} from "../../../admin/tree/Tree";
import { runInAction, toJS } from "mobx";
import {
  convertFlatToTree,
  convertTreeToFlat,
} from "../../../../utils/DataUtilities";
import WatsonApi from "../../../../backends/WatsonApi";
import NotificationManager from "../../../notifications/NotificationManager";
import { ReactiveAccordionItem } from "../../../schoolfeed/ReactiveAccordionItem";
import dynamic from "next/dynamic";
import { useStores } from "../../../../stores/useStores";

const SortableTreeWithoutDndContext = dynamic(
  () => import("@nosferatu500/react-sortable-tree"),
);

const AccordionTerrace = observer(
  (props: { blockObj: IAccordionTerrace; aboveTheFold: boolean }) => {
    const { organizationStore } = useStores();
    const [reorder, setReorder] = useState(false);
    const [addingNewAccordionItem, setAddingNewAccordionItem] = useState(false);

    const accordionTerraceContainerClassName = classNames({
      [styles.container]: true,
    });
    const [expanded, setExpanded] = useState({});
    const [searchTerm, setSearchTerm] = useState("");
    const [previousSearchTerm, setPreviousSearchTerm] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [isSearchExpanded, setIsSearchExpanded] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    let accordionList = convertFlatToTree(props.blockObj.items) || [];

    const toggleExpansion = (id: number) => {
      if (expanded[id]) {
        setExpanded({ ...expanded, [id]: false });
      } else {
        setExpanded({ ...expanded, [id]: true });
      }
    };

    const expandAll = () => {
      setExpanded(
        accordionList.reduce(
          (acc, item) => {
            acc[item.id] = true;
            return acc;
          },
          {} as Record<string, boolean>,
        ),
      );
    };

    const collapseAll = () => {
      setExpanded(
        accordionList.reduce(
          (acc, item) => {
            acc[item.id] = false;
            return acc;
          },
          {} as Record<string, boolean>,
        ),
      );
    };

    const allAreExpanded = accordionList.every((item) => expanded[item.id]);

    const reorderClassName = classNames({
      [styles.reorder]: reorder,
    });

    const handleCancel = () => {
      setAddingNewAccordionItem(false);
    };

    const reorderAccordionDropDownItem = {
      title: "Reorder",
      icon: (
        <FontAwesome
          prefix={"fas"}
          fixedWidth
          name={"fa-arrows-up-down"}
          ariaHidden
          className={reorderClassName}
        />
      ),
      onClick: () => setReorder((v) => !v),
      hideButtonSelector: true,
    };

    const accordionTerraceDropDownItems: EditDropDownItem[] = [
      {
        title: "Add Item",
        icon: <FontAwesome prefix={"fas"} fixedWidth name={"fa-plus"} />,
        onClick: () => setAddingNewAccordionItem(true),
      },
    ];

    if (accordionList?.length > 1) {
      accordionTerraceDropDownItems.push(reorderAccordionDropDownItem);
    }

    const handleSearch = async () => {
      if (searchTerm === "") return;
      setIsSearching(true);
      setIsLoading(true);
      try {
        const client = await WatsonApi();
        const response =
          await client.apis.organizations.organizations_search_list({
            organization_pk: organizationStore.organization.id,
            query: searchTerm,
            limit_results: ["objects"],
            "objects.content_type": "accordionitems",
            "objects.page_id": props.blockObj.organization_id,
          });
        const parsed = JSON.parse(response.data);
        setSearchResults(parsed.objects.data);
        setPreviousSearchTerm(searchTerm);
      } catch (e) {
        console.error(e);
      }
      setIsLoading(false);
    };

    const ExpandAllButton = observer(
      (props: { text: string; toggleAll?: () => void }) => {
        return (
          <button
            className={styles.expandAllButtonContainer}
            onClick={props.toggleAll}
          >
            <span>{props.text}</span>
          </button>
        );
      },
    );

    const [height, setHeight] = useState(accordionList.length * 50);

    useEffect(() => {
      setHeight(accordionList.length * 50);
    }, [accordionList.length]);

    const searchAndExpandButtonStyles = classNames({
      [styles.searchAndExpandButtonContainer]: true,
      [styles.reorderActive]: reorder,
    });

    const handleMoveNode = async (obj) => {
      const previousFlatState: IAccordionItem[] =
        toJS(props.blockObj.items) || [];
      try {
        const nextFlatState: IAccordionItem[] = convertTreeToFlat(
          obj.treeData,
          "",
        );
        const updatedItems: IAccordionItem[] = getUpdatedItemPathsFromMove(
          previousFlatState,
          nextFlatState,
        );
        if (updatedItems.length === 0) return;

        const client = await WatsonApi();
        await client.apis.organizations.organizations_terraces_accordion_items_bulk_partial_update(
          {
            organization_pk: props.blockObj.organization_id,
            terrace_pk: props.blockObj.id,
          },
          {
            requestBody: updatedItems.map((item) => ({
              id: item.id,
              path: item.path,
            })),
          },
        );

        runInAction(() => {
          props.blockObj.items = nextFlatState.map(
            (item) => new ReactiveAccordionItem(item),
          );
        });
        NotificationManager.success(
          "Accordion Terrace successfully reordered!",
        );
      } catch (e) {
        NotificationManager.error(e.message);
        runInAction(() => {
          props.blockObj.items = previousFlatState;
        });
      }
    };

    const searchAndExpandButton = (
      <div className={searchAndExpandButtonStyles}>
        <div className={styles.searchBoxContainer}>
          <SearchBox
            searchTerm={previousSearchTerm}
            placeholder={`Search ${props.blockObj.title}`}
            isValidating={false}
            inputValue={searchTerm}
            setInputValue={setSearchTerm}
            handleSubmit={handleSearch}
          />
        </div>
        {accordionList.length > 1 && !isSearching && !isSearchExpanded ? (
          <ExpandAllButton
            text={allAreExpanded ? "Collapse All" : "Expand All"}
            toggleAll={allAreExpanded ? collapseAll : expandAll}
          />
        ) : null}
      </div>
    );

    const handleSave = async (
      item: IAccordionItem,
    ): Promise<IAccordionItem> => {
      let lastItemNum = 0;
      if ((props.blockObj.items as IAccordionItem[]).length > 0) {
        const lastItemPath = (props.blockObj.items as IAccordionItem[])[
          (props.blockObj.items as IAccordionItem[]).length - 1
        ].path.split(".")[0];
        lastItemNum = parseInt(
          lastItemPath.slice(lastItemPath.length - 3, lastItemPath.length),
        );
      }
      const path = `${(lastItemNum + 1).toString().padStart(3, "0")}`;

      const client = await WatsonApi();
      const response =
        await client.apis.organizations.organizations_terraces_accordion_items_create(
          {
            organization_pk: props.blockObj.organization_id,
            terrace_pk: props.blockObj.id,
          },
          {
            requestBody: {
              title: item.title,
              description: item.description,
              content_html: item.content_html,
              modal_enabled: item.modal_enabled,
              path,
            },
          },
        );
      const updatedItem = new ReactiveAccordionItem(JSON.parse(response.data));
      (props.blockObj.items as IAccordionItem[]).push(updatedItem);
      props.blockObj.items.sort((a, b) => a.path.localeCompare(b.path));
      return updatedItem;
    };

    return (
      <article>
        <BlockTitle
          blockObj={props.blockObj}
          aboveTheFold={props.aboveTheFold}
          allowSave={true}
          dropDownItems={accordionTerraceDropDownItems}
          showViewMore={false}
          rightElement={searchAndExpandButton}
          rightElementClassName={styles.searchAndButtonContainer}
        />
        <div className={accordionTerraceContainerClassName}>
          {!reorder ? (
            <>
              {isSearching && (
                <AccordionItemSearchResultList
                  results={searchResults}
                  setIsSearching={setIsSearching}
                  searchTerm={searchTerm}
                  setSearchTerm={setSearchTerm}
                  previousSearchTerm={previousSearchTerm}
                  setIsExpanded={setIsSearchExpanded}
                  isLoading={isLoading}
                  blockObj={props.blockObj}
                />
              )}
              {!isSearching && accordionList?.length > 0 && (
                <ul className={styles.accordionList}>
                  {accordionList?.map((accordionItem) => {
                    const itemIndex = props.blockObj?.items?.findIndex(
                      (item) => item.id === accordionItem.id,
                    );
                    return (
                      <AccordionItem
                        key={accordionItem.id}
                        totalItemCount={props.blockObj?.items?.length || 0}
                        getItemAtIndex={(idx) => {
                          return props.blockObj.items.length >= idx
                            ? (props.blockObj.items[
                                idx
                              ] as ReactiveAccordionItem)
                            : null;
                        }}
                        initialItemIndex={itemIndex}
                        item={accordionItem}
                        accordionList={accordionList}
                        index={itemIndex}
                        blockObj={props.blockObj}
                        handleCancel={handleCancel}
                        expanded={expanded[accordionItem.id]}
                        toggleExpansion={toggleExpansion}
                      />
                    );
                  })}
                </ul>
              )}
            </>
          ) : (
            <SortableTreeWithoutDndContext
              style={{ height: height, backgroundColor: "white" }}
              treeData={accordionList}
              onChange={() => {}}
              onVisibilityToggle={({ treeData, node, expanded }) => {
                props.blockObj.items = props.blockObj.items.map(
                  (n: IAccordionItem): IAccordionItem => {
                    if (n.id === node.id) {
                      n.expanded = expanded;
                    }
                    return n;
                  },
                );
                setHeight(numExpandedNodes(treeData) * 60 + 20);
              }}
              onMoveNode={handleMoveNode}
              theme={AccordionReorderingTheme}
              rowHeight={60}
              scaffoldBlockPxWidth={30}
              getNodeKey={({ node }) => node.id}
              maxDepth={2}
            />
          )}
          {!isSearching && addingNewAccordionItem && (
            <div className={styles.accordionItem}>
              <AccordionItemEditor
                blockObj={props.blockObj}
                index={accordionList?.length || 0}
                handleCancel={handleCancel}
                handleSave={handleSave}
              />
            </div>
          )}
        </div>
      </article>
    );
  },
);

export default AccordionTerrace;
